// React
import React from 'react';

// Mantine
import {
  Group,
  Flex,
  Text,
  Badge,
  Stack,
  Skeleton,
  useComputedColorScheme,
  useMantineTheme,
} from '@mantine/core';

// Custom Components
import { StyledPaper } from 'components/common/StyledPaper';

export interface ProviderBreakdownItem {
  providerID: string;
  firstName: string;
  lastName: string;
  count: number;
}

export interface ClaimMetricBaseProps {
  title: string;
  subtitle?: string;
  total?: number; // total claims in this status
  providers?: ProviderBreakdownItem[]; // top providers
  loading: boolean;
  badgeColour?: string;
  onClick?: (providerID: string) => void;
  icon: React.ComponentType<any>;
  path?: string;
  headerLabel?: string; // e.g. 'Top Providers'
  emptyLabel?: string; // custom empty state message
  dataTestId?: string; // test id override
}

export const ClaimMetricBase: React.FC<ClaimMetricBaseProps> = ({
  title,
  total,
  providers = [],
  loading,
  badgeColour,
  onClick,
  subtitle,
  icon: Icon,
  headerLabel = 'Top Providers',
  emptyLabel = 'No provider data',
  dataTestId = 'provider-metric-card',
}) => {
  const colorScheme = useComputedColorScheme('dark');
  const theme = useMantineTheme();
  const isDark = colorScheme === 'dark';
  const subtleText = isDark ? theme.colors.gray[5] : theme.colors.gray[6];

  return (
    <StyledPaper testId={dataTestId} isLoading={loading}>
      <Group justify="space-between" align="center" mb={12}>
        <div>
          <Text fw={600} fz="xs" c={subtleText} tt="uppercase" lts={0.8}>
            {headerLabel}
          </Text>
          <Stack gap={6} mt={4}>
            <Text fz="xl" fw={700}>
              {title}
            </Text>
            <Group>
              {subtitle && (
                <Text fz="sm" fs="italic" c={subtleText}>
                  {subtitle}
                </Text>
              )}
              {typeof total === 'number' && (
                <Badge
                  radius="xl"
                  variant={isDark ? 'light' : 'outline'}
                  size="sm"
                  color={badgeColour}
                >
                  {total}
                </Badge>
              )}
            </Group>
          </Stack>
        </div>
        <Flex
          align="center"
          justify="center"
          w={40}
          h={40}
          style={{
            borderRadius: '999px',
            background: isDark
              ? 'rgba(148,163,184,0.16)'
              : 'rgba(15,23,42,0.06)',
          }}
        >
          <Icon
            size="1.4rem"
            stroke={2.2}
            style={{
              color: isDark ? theme.colors.gray[2] : theme.colors.dark[6],
            }}
          />
        </Flex>
      </Group>
      {loading ? (
        <Stack data-testid="metric-card-skeleton" gap={'sm'}>
          <Skeleton height={18} />
          <Skeleton height={18} />
          <Skeleton height={18} />
          <Skeleton height={18} />
        </Stack>
      ) : (
        <Flex direction="column" gap={6} mt={4}>
          {providers.length === 0 && (
            <Text c={subtleText} size="sm">
              {emptyLabel}
            </Text>
          )}
          {providers.length > 0 && (
            <Group justify="space-between" c={subtleText} fw={500} gap={4}>
              <Text size="xs" style={{ flex: 1 }}>
                Provider
              </Text>
              <Text size="xs" w={70} ta="right">
                Claims
              </Text>
            </Group>
          )}
          {providers.map((p, i) => (
            <Group
              key={p.providerID}
              justify="space-between"
              gap={4}
              data-testid="provider-metric-row"
            >
              <Text
                size="sm"
                // Limit interactive/clickable area to the provider name text only
                onClick={(e) => {
                  if (onClick) {
                    e.preventDefault();
                    e.stopPropagation();
                    onClick(`${p.firstName} ${p.lastName}`);
                  }
                }}
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  flex: 1,
                  cursor: onClick ? 'pointer' : 'default',
                }}
              >
                {i + 1}. {p.firstName} {p.lastName}
              </Text>
              <Text size="sm" w={70} ta="right" fw={600}>
                {p.count}
              </Text>
            </Group>
          ))}
        </Flex>
      )}
    </StyledPaper>
  );
};
